<template>
  <div>
    <app-modal
      v-model="isDialogOpened"
      size="lg"
      @cancel="explicitCloseDialog"
    >
      <template #title>{{ t('title') }}</template>
      <template #body>
        <p class="mb-4">{{ t('witnessesLimitPerSigner') }}</p>

        <app-alert-in-page
          v-if="!canAddRecipient"
          variant="warning"
          :dismissible="false"
          class="mb-4"
        >
          {{ t('recipientsLimitReachedWarning', { recipientsMaxCount: ENVELOPE_RECIPIENTS_MAX_COUNT }) }}
        </app-alert-in-page>

        <WitnessSelectionCard
          v-for="(witnessSelection, i) in localWitnessArray"
          :key="`witness-${i}`"
          :witness-selection="witnessSelection"
          :signers="getSignersList(witnessSelection.signer)"
          :selected-signers="selectedSigners"
          :witnesses="getWitnessesList(witnessSelection.signer)"
          :can-delete="canDeleteGroup"
          :can-add-recipient="canAddRecipient"
          :index="Number(i)"
          @add-signer="addSigner(witnessSelection, $event)"
          @add-witness="addWitness(witnessSelection, $event)"
          @delete="onDelete(i)"
        />
        <app-button
          v-show="canAddSigner"
          type="outlined"
          variant="neutral"
          full-width
          class="mt-4"
          @click="onAddSignatoryClick"
        >
          <template #left-icon>
            <app-material-icon name="add" />
          </template>
          {{ t('addSignatory') }}
        </app-button>
      </template>
      <template #footer>
        <div>
          <AppTooltip>
            <template #activator="{ props }">
              <div v-bind="props" class="d-inline-block">
                <app-button
                  type="outlined"
                  variant="neutral"
                  class="mr-2"
                  @click="onResetWitnessClick"
                >
                  {{ t('resetWitnesses') }}
                </app-button>
              </div>
            </template>
            <span>{{ t('resetWitnessesTooltip') }}</span>
          </AppTooltip>
        </div>
        <div class="d-flex justify-end w-100">
          <app-button
            type="outlined"
            variant="neutral"
            class="mr-2"
            @click="explicitCloseDialog"
          >
            {{ $t('common.cancel') }}
          </app-button>
          <app-button
            :disabled="shouldDisabledValidateButton"
            @click="validateWitnessesSelection"
          >
            {{ $t('common.validate') }}
          </app-button>
        </div>
      </template>
    </app-modal>
    <AppUnsavedChangesDialog
      v-if="isUnsavedChangesDialogOpen"
      @save="onSave"
      @discard="closeDialog"
      @close="isUnsavedChangesDialogOpen = false"
    >
      <template #header-text>{{ t('unsavedChanges.header') }}</template>
      <template #body-text>{{ t('unsavedChanges.body') }}</template>
    </AppUnsavedChangesDialog>
  </div>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { cloneDeep, uniq } from 'lodash-es'
import { defineComponent, PropType } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import AppUnsavedChangesDialog from '@/common/dialogs/AppUnsavedChangesDialog.vue'
import { ENVELOPE_RECIPIENTS_MAX_COUNT } from '@/common/enveloppe/envelope-recipients'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import AppAlertInPage from '@/design-system/alert/AppAlertInPage.vue'
import { dialogMixin } from '@/mixins/dialogMixin'
import { SignerWitnesses } from '@/project/signing-checklist/witness/types'
import WitnessSelectionCard from '@/project/signing-checklist/witness/WitnessSelectionCard.vue'
import { VALIDATE_WITNESSES_SELECTIONS } from '@/store/modules/signing-checklist/action_types'
import { REMOVE_SIGNER_WITNESSES } from '@/store/modules/signing-checklist/mutation_types'

type DataType = {
  ENVELOPE_RECIPIENTS_MAX_COUNT: number
  localWitnessArray: SignerWitnesses[]
  isUnsavedChangesDialogOpen: boolean
  hasUnsavedChanges: boolean
  hasReset: boolean
}

export default defineComponent({
  name: 'WitnessDialog',
  components: {
    AppAlertInPage,
    AppUnsavedChangesDialog,
    AppTooltip,
    WitnessSelectionCard,
  },
  mixins: [dialogMixin],
  setup () {
    const { t } = useTranslation({ keyPrefix: 'project.signing-checklist.witness.WitnessDialog' })

    return {
      t,
      v$: useVuelidate(),
    }
  },
  props: {
    signers: {
      type: Array as PropType<number[][]>,
      required: true,
    },
    fileId: {
      type: Number,
      required: true,
    },
    witnesses: {
      type: Array as PropType<SignerWitnesses[]>,
    },
  },
  data (): DataType {
    return {
      ENVELOPE_RECIPIENTS_MAX_COUNT,
      localWitnessArray: [{ signer: null, witness: [] }],
      isUnsavedChangesDialogOpen: false,
      hasUnsavedChanges: false,
      hasReset: false,
    }
  },
  computed: {
    ...mapGetters('groups', ['allRoomMembers']),
    areEveryGroupsValid (): boolean {
      return this.localWitnessArray.every(i => {
        return i.signer !== null && i.witness.length
      })
    },
    canAddRecipient (): boolean {
      const witnesses = this.localWitnessArray.map(signerWithWitnesses => signerWithWitnesses.witness).flat()
      const currentRecipientsCount = uniq([
        ...witnesses,
        ...this.signers.flat(),
      ]).length

      return currentRecipientsCount < ENVELOPE_RECIPIENTS_MAX_COUNT
    },
    canAddSigner (): boolean {
      return this.areEveryGroupsValid && this.thereAreRemainingSigners && this.canAddRecipient
    },
    selectedSigners (): number[] {
      const selectedSigners: number[] = []
      this.localWitnessArray.forEach(o => {
        if (o.signer) {
          selectedSigners.push(o.signer)
        }
      })
      return selectedSigners
    },
    thereAreRemainingSigners (): boolean {
      return this.signers.flat().some(signer => {
        return this.localWitnessArray.every(witness => witness.signer !== signer && !witness.witness.includes(signer))
      })
    },
    canDeleteGroup (): boolean {
      return this.localWitnessArray.length >= 2
    },
    isNoSignerOrWitness () {
      return this.localWitnessArray.every((item: SignerWitnesses) =>
        item.signer === null && Array.isArray(item.witness) && item.witness.length === 0,
      )
    },
    shouldDisabledValidateButton () {
      if (this.hasReset || this.isNoSignerOrWitness) return false
      if (this.v$.$invalid) return true
      if (this.v$.$anyDirty) return false
      if (this.hasUnsavedChanges) return false
      return true
    },
  },
  created () {
    if (this.witnesses && this.witnesses.length) {
      this.localWitnessArray = cloneDeep(this.witnesses)
    }
  },
  methods: {
    ...mapActions('signingChecklist', [VALIDATE_WITNESSES_SELECTIONS]),
    ...mapMutations('signingChecklist', [REMOVE_SIGNER_WITNESSES]),
    onAddSignatoryClick () {
      this.localWitnessArray.push({ signer: null, witness: [] })
    },
    addSigner (witnessSelection: SignerWitnesses, event: number) {
      witnessSelection.signer = event
      this.hasReset = false
    },
    addWitness (witnessSelection: SignerWitnesses, event: Array<number>) {
      witnessSelection.witness = event
      this.hasReset = false
    },
    validateWitnessesSelection () {
      this.v$.$touch()
      if (!this.v$.$invalid || this.hasReset || this.isNoSignerOrWitness) {
        this.VALIDATE_WITNESSES_SELECTIONS({
          importedFileId: this.fileId,
          signersWithWitnesses: this.localWitnessArray,
        })

        if (this.hasReset) {
          toast.success(this.$t('project.signing-checklist.witness.WitnessDialog.confirmResetWitnesses'))
        }
        this.closeDialog()
      }
    },
    onDelete (index: number) {
      this.localWitnessArray.splice(index, 1)
      this.hasUnsavedChanges = true
    },
    getSignersList (signerId: number | null): Member[] {
      const signersList = (member: Member) => {
        return this.signers.flat().includes(member.id)
      }
      return this.allRoomMembers
        .filter(signersList)
        .filter((member: Member) => this.memberAlreadyWitnessForOtherSigner(member, signerId))
    },
    getWitnessesList (signerId: number | null): Member[] {
      const memberAlreadySignerWithWitnesses = (member: Member)  => {
        return this.localWitnessArray.every(signerWithWitnesses => signerWithWitnesses.signer !== member.id)
      }
      return this.allRoomMembers
        .filter(memberAlreadySignerWithWitnesses)
        .filter((member: Member) => this.memberAlreadyWitnessForOtherSigner(member, signerId))
    },
    memberAlreadyWitnessForOtherSigner (member: Member, signerId: number | null): boolean {
      return this.localWitnessArray
        .filter(signerWithWitnesses => signerWithWitnesses.signer !== signerId)
        .flatMap(signerWithWitnesses => signerWithWitnesses.witness)
        .every(witness => witness !== member.id)
    },
    explicitCloseDialog () {
      if (!this.shouldDisabledValidateButton || this.hasReset) {
        this.isUnsavedChangesDialogOpen = true
      } else {
        this.closeDialog()
      }
    },
    onSave () {
      this.validateWitnessesSelection()
      this.isUnsavedChangesDialogOpen = false
    },
    onResetWitnessClick () {
      this.v$.$reset()
      this.localWitnessArray = [{ signer: null, witness: [] }]
      this.hasReset = true
    },
  },
})
</script>
